import { type ReactNode, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Loader2, Package, Plus, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import { configurationOptionName, configurationQueryOptions, type ProductConfiguration } from '../catalog/queries'
import { CatalogConfigurationDrawer } from '../editor/configuration-drawers'
import { AiData } from '../ui/ai-data'
import { rowSuggestions } from './api'
import type { ConfigurationSuggestion, ImportSuggestion, MatchCandidate, ReviewedRow } from './types'

// What the import suggests for a row's chosen product (USA-1341): the options it chose or needs
// confirmed, and accessories to add as sub-lines, each with its reason. Suggestions follow the
// product: the import's own for a recommended product, else read for the product picked by hand.

const SHOWN_ACCESSORIES = 3

export type RowProduct = { id: number; manufacturer: string; model: string; title: string }

type OptionLine = {
  group: string
  label: string | null
  reason: string | null
  source: ConfigurationSuggestion['source']
  chosen: boolean
}

function optionLabel(configuration: ProductConfiguration | undefined, group: string, optionId: string) {
  const option = configuration?.groups.find((item) => item.name === group)?.options.find((item) => item.id === optionId)
  return option ? configurationOptionName(option) : null
}

function defaultOption(configuration: ProductConfiguration | undefined, group: string) {
  return configuration?.groups.find((item) => item.name === group)?.options.find((item) => item.is_default && item.available)?.id
}

/** Options to show: the import's choices, then what it asks Sales to confirm, then any other option
 * Sales moved off its default. A choice made in review replaces the import's for its group. */
function optionLines(
  chosenByImport: ConfigurationSuggestion[],
  suggestions: ImportSuggestion[],
  chosen: Record<string, string>,
  configuration: ProductConfiguration | undefined,
): OptionLine[] {
  const lines: OptionLine[] = []
  const seen = new Set<string>()
  for (const item of chosenByImport) {
    seen.add(item.group)
    const override = chosen[item.group] !== undefined && chosen[item.group] !== item.option_id
    lines.push({
      group: item.group,
      label: override ? optionLabel(configuration, item.group, chosen[item.group]!) : item.label,
      reason: override ? null : (item.reason ?? null),
      source: item.source,
      chosen: override,
    })
  }
  for (const item of suggestions) {
    if (item.kind !== 'option' || !item.group || seen.has(item.group)) continue
    seen.add(item.group)
    const value = chosen[item.group]
    lines.push({
      group: item.group,
      label: value ? optionLabel(configuration, item.group, value) : null,
      reason: value ? null : item.reason,
      source: item.source,
      chosen: Boolean(value),
    })
  }
  for (const [group, value] of Object.entries(chosen)) {
    if (seen.has(group) || value === defaultOption(configuration, group)) continue
    lines.push({ group, label: optionLabel(configuration, group, value), reason: null, source: 'catalog', chosen: true })
  }
  return lines
}

/** Catalog titles usually carry the brand and model already; name them once. */
function productName(product: { manufacturer: string; model: string; title: string }) {
  return product.title.includes(product.model) ? product.title : `${product.manufacturer} ${product.model} · ${product.title}`
}

function Marked({ ai, label, children, className }: { ai: boolean; label: string; children: ReactNode; className?: string }) {
  return ai ? (
    <AiData asChild className={cn('px-2.5', className)} label={label}>
      <li>{children}</li>
    </AiData>
  ) : (
    <li className={className}>{children}</li>
  )
}

export function RowSuggestions({
  importId,
  row,
  product,
  candidate,
  configuration,
  accessories,
  disabled,
  onConfigure,
  onToggleAccessory,
}: {
  importId: string
  row: ReviewedRow
  product: RowProduct
  /** The import's recommendation for this product, when it made one. */
  candidate?: MatchCandidate
  configuration: Record<string, string>
  accessories: number[]
  disabled: boolean
  onConfigure: (configuration: Record<string, string>) => void
  onToggleAccessory: (productId: number) => void
}) {
  const api = useQuoteBuilderApi()
  const [configuring, setConfiguring] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const chosenByImportStored = row.configuration_suggestions?.[String(product.id)]
  // Imports read before USA-1341 kept text notes instead of suggestions; read those afresh.
  const stored = candidate && chosenByImportStored && !(candidate.notes ?? []).length
  const read = useQuery({
    queryKey: ['import-row-suggestions', importId, row.row_id, product.id],
    queryFn: ({ signal }) => rowSuggestions(api, importId, row.row_id, product.id, signal),
    enabled: !stored,
    staleTime: Infinity,
  })
  const hasChoices = Object.keys(configuration).length > 0
  const options = useQuery({ ...configurationQueryOptions(api, product.id), enabled: hasChoices })
  const chosenByImport = stored ? chosenByImportStored : (read.data?.configuration_suggestions ?? [])
  const suggestions = stored ? (candidate.suggestions ?? []) : (read.data?.suggestions ?? [])
  const lines = optionLines(chosenByImport, suggestions, configuration, options.data)
  const offered = suggestions.filter((item) => item.kind === 'accessory' && item.product)
  const shown = showAll ? offered : offered.slice(0, SHOWN_ACCESSORIES)
  const initialChoices = {
    ...Object.fromEntries(chosenByImport.map((item) => [item.group, item.option_id])),
    ...Object.fromEntries(
      suggestions.flatMap((item) => (item.kind === 'option' && item.group && item.option_id ? [[item.group, item.option_id]] : [])),
    ),
    ...configuration,
  }

  if (!stored && read.isPending) {
    return (
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
        <Loader2 aria-hidden="true" className="size-3.5 animate-spin motion-reduce:animate-none" />
        Checking options
      </p>
    )
  }
  if (!stored && read.isError) {
    return (
      <p className="mt-3 flex flex-wrap items-center gap-2 text-xs text-muted-foreground" role="alert">
        Options and accessories could not be loaded.
        <Button className="h-7 px-2" onClick={() => void read.refetch()} size="sm" type="button" variant="outline">
          Retry
        </Button>
      </p>
    )
  }
  if (!lines.length && !offered.length) return null

  return (
    <div className="mt-3 space-y-3">
      {lines.length ? (
        <section aria-label={`Options for ${product.manufacturer} ${product.model}`}>
          <p className="mb-1 text-xs font-medium text-muted-foreground">Options</p>
          <ul className="space-y-1">
            {lines.map((line) => (
              <Marked
                ai={line.source === 'ai' && !line.chosen}
                className="flex min-h-9 flex-wrap items-center gap-x-3 gap-y-0.5 py-1"
                key={line.group}
                label="AI option suggestion"
              >
                {line.label === null && !line.chosen ? (
                  <Button disabled={disabled} onClick={() => setConfiguring(true)} size="sm" type="button" variant="outline">
                    <SlidersHorizontal aria-hidden="true" />
                    Confirm {line.group.toLocaleLowerCase()}
                  </Button>
                ) : (
                  <span className="flex min-w-0 basis-full items-center gap-1.5 text-sm">
                    {line.chosen ? <Check aria-label="Chosen" className="size-3.5 shrink-0 text-emerald-600" /> : null}
                    <span className="text-muted-foreground">{line.group}</span>
                    <span className="font-medium [overflow-wrap:anywhere]">{line.label ?? '…'}</span>
                    <Button
                      aria-label={`Change ${line.group.toLocaleLowerCase()}`}
                      className="h-7 px-1.5 text-xs"
                      disabled={disabled}
                      onClick={() => setConfiguring(true)}
                      size="sm"
                      type="button"
                      variant="link"
                    >
                      Change
                    </Button>
                  </span>
                )}
                {line.reason ? <span className="text-xs text-muted-foreground">{line.reason}</span> : null}
              </Marked>
            ))}
          </ul>
        </section>
      ) : null}
      {offered.length ? (
        <section aria-label={`Accessories for ${product.manufacturer} ${product.model}`}>
          <p className="mb-1 text-xs font-medium text-muted-foreground">Accessories</p>
          <ul className="space-y-1">
            {shown.map((item) => {
              const accessory = item.product!
              const added = accessories.includes(accessory.product_id)
              return (
                <Marked
                  ai={item.source === 'ai'}
                  className="flex items-center gap-3 py-1.5"
                  key={accessory.product_id}
                  label="AI accessory suggestion"
                >
                  <span className="grid size-10 shrink-0 place-items-center overflow-hidden rounded-md border bg-white">
                    {accessory.image_url ? (
                      <img alt="" className="size-full object-contain" loading="lazy" src={accessory.image_url} />
                    ) : (
                      <Package aria-hidden="true" className="size-4 text-muted-foreground" />
                    )}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-sm font-medium">{productName(accessory)}</span>
                    <span className="block text-xs text-muted-foreground">{item.reason}</span>
                  </span>
                  <Button
                    aria-label={`${added ? 'Remove' : 'Add'} ${accessory.manufacturer} ${accessory.model}`}
                    aria-pressed={added}
                    className="shrink-0"
                    disabled={disabled}
                    onClick={() => onToggleAccessory(accessory.product_id)}
                    size="sm"
                    type="button"
                    variant={added ? 'secondary' : 'outline'}
                  >
                    {added ? <Check aria-hidden="true" className="text-emerald-600" /> : <Plus aria-hidden="true" />}
                    {added ? 'Added' : 'Add'}
                  </Button>
                </Marked>
              )
            })}
          </ul>
          {offered.length > SHOWN_ACCESSORIES ? (
            <Button className="h-7 px-1.5 text-xs" onClick={() => setShowAll((current) => !current)} size="sm" type="button" variant="link">
              {showAll ? 'Show fewer' : `Show ${offered.length - SHOWN_ACCESSORIES} more`}
            </Button>
          ) : null}
        </section>
      ) : null}
      {configuring ? (
        <CatalogConfigurationDrawer
          actionLabel="Confirm options"
          initialChoices={initialChoices}
          onAdd={(choices) => {
            setConfiguring(false)
            onConfigure(choices)
          }}
          onCancel={() => setConfiguring(false)}
          product={product}
          title="Options"
        />
      ) : null}
    </div>
  )
}
